import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Inbox, RefreshCw, Mail, MailCheck, Send, Sparkles, Loader2, Link2, Unlink, CheckCircle2, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";
import { gmail } from "@/lib/api";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function SupportInbox() {
  const { state } = useStore();
  const nora = state.employees.find((e) => e.id === "nora");
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState(null); // {connected,email,auto_reply,configured}
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null); // full message
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [draft, setDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [savingAck, setSavingAck] = useState(false);

  const loadStatus = useCallback(async () => {
    try { setStatus(await gmail.status()); } catch (_) { setStatus({ connected: false, configured: true }); }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoadingList(true);
    try {
      const { messages } = await gmail.messages();
      setMessages(messages || []);
    } catch (e) {
      toast.error("Could not load the inbox. Reconnect if this persists.");
    } finally { setLoadingList(false); }
  }, []);

  // Handle OAuth return + initial load
  useEffect(() => {
    const g = params.get("gmail");
    if (g === "connected") { toast.success("Support inbox connected"); params.delete("gmail"); setParams(params, { replace: true }); }
    else if (g === "error") { toast.error("Gmail connection was cancelled or failed"); params.delete("gmail"); setParams(params, { replace: true }); }
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runProcess = useCallback(async () => {
    try {
      const r = await gmail.process();
      if (r.replied > 0) toast.success(`Nora auto-replied to ${r.replied} new email${r.replied > 1 ? "s" : ""}`);
      else if (r.acked > 0) toast.success(`Nora acknowledged ${r.acked} new email${r.acked > 1 ? "s" : ""}`);
      if (r.replied > 0 || r.acked > 0) loadMessages();
      return r;
    } catch (_) { return null; }
  }, [loadMessages]);

  useEffect(() => {
    if (status?.connected) {
      (async () => {
        await runProcess();
        loadMessages();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.connected]);

  const openMessage = async (m) => {
    setSelected({ id: m.id, subject: m.subject, from: m.from, loading: true });
    setDraft("");
    setLoadingMsg(true);
    try {
      const full = await gmail.message(m.id);
      setSelected(full);
      if (full.replied && full.replyText) setDraft(full.replyText);
    } catch (_) { toast.error("Could not open that email"); }
    finally { setLoadingMsg(false); }
  };

  const generateDraft = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const { draft } = await gmail.draft(selected.id);
      setDraft(draft);
    } catch (_) { toast.error("Nora couldn't draft a reply right now"); }
    finally { setDrafting(false); }
  };

  const sendReply = async () => {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      await gmail.reply(selected.id, draft);
      toast.success(`Reply sent to ${selected.fromEmail}`);
      setSelected((s) => ({ ...s, replied: true, replyText: draft }));
      setMessages((ms) => ms.map((m) => (m.id === selected.id ? { ...m, replied: true } : m)));
    } catch (_) { toast.error("Could not send the reply"); }
    finally { setSending(false); }
  };

  const toggleAck = async () => {
    const next = !status.auto_ack;
    setSavingAck(true);
    try {
      await gmail.setSettings({ auto_ack: next });
      setStatus((s) => ({ ...s, auto_ack: next }));
      if (next) { toast.success("Nora will auto-acknowledge new emails"); await runProcess(); }
      else toast.success("Acknowledgements turned off");
    } catch (_) { toast.error("Could not update acknowledgements"); }
    finally { setSavingAck(false); }
  };

  const toggleAuto = async () => {
    const next = !status.auto_reply;
    setSavingAuto(true);
    try {
      await gmail.setSettings({ auto_reply: next });
      setStatus((s) => ({ ...s, auto_reply: next }));
      if (next) { toast.warning("Auto-reply ON — Nora will now send full replies to new emails automatically"); await runProcess(); }
      else toast.success("Auto-reply turned off");
    } catch (_) { toast.error("Could not update auto-reply"); }
    finally { setSavingAuto(false); }
  };

  const disconnect = async () => {
    try { await gmail.disconnect(); toast.success("Disconnected the support inbox"); setStatus((s) => ({ ...s, connected: false, email: null })); setMessages([]); setSelected(null); } catch (_) {}
  };

  // ---- Not connected view --------------------------------------------------
  if (status && !status.connected) {
    return (
      <div>
        <PageHeader nora={nora} email={null} />
        <div className="mx-auto mt-10 max-w-xl rounded-3xl border border-white/10 bg-[#0A1326]/70 p-8 text-center" data-testid="gmail-connect-card">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#00E5FF]/10 text-[#00E5FF]"><Mail size={26} /></div>
          <h2 className="mt-4 font-display text-xl font-semibold text-white">Connect the Support inbox</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-slate-400">
            Give Nora access to <span className="text-slate-200">cardiac.solutions.support@gmail.com</span> so she can read
            incoming support emails, summarise them, and draft replies.
          </p>
          <a
            href={gmail.loginUrl()}
            data-testid="gmail-connect-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-5 py-2.5 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5"
          >
            <Link2 size={16} /> Connect Gmail
          </a>
          <p className="mt-4 text-[11px] text-slate-500">Sign in with the cardiac.solutions.support@gmail.com account when prompted.</p>
        </div>
      </div>
    );
  }

  // ---- Connected / loading view -------------------------------------------
  return (
    <div>
      <PageHeader nora={nora} email={status?.email} onRefresh={loadMessages} refreshing={loadingList}>
        <button
          onClick={toggleAck}
          disabled={savingAck}
          data-testid="ack-toggle"
          className={cn(
            "flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
            status?.auto_ack ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/12 text-slate-300 hover:text-white"
          )}
          title="When on, Nora emails a quick 'got it' acknowledgement for every new message"
        >
          {savingAck ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />}
          Acknowledge {status?.auto_ack ? "ON" : "OFF"}
        </button>
        <button
          onClick={toggleAuto}
          disabled={savingAuto}
          data-testid="auto-reply-toggle"
          className={cn(
            "flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
            status?.auto_reply ? "border-[#FF3B5C]/50 bg-[#FF3B5C]/10 text-[#FF3B5C]" : "border-white/12 text-slate-300 hover:text-white"
          )}
          title="When on, Nora sends replies automatically"
        >
          {savingAuto ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
          Auto-reply {status?.auto_reply ? "ON" : "OFF"}
        </button>
        <button onClick={disconnect} data-testid="gmail-disconnect-btn" className="flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5 text-xs text-slate-400 hover:text-white">
          <Unlink size={13} /> Disconnect
        </button>
      </PageHeader>

      {status?.auto_reply && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#FF3B5C]/30 bg-[#FF3B5C]/10 px-4 py-2 text-xs text-[#FFB4C0]" data-testid="auto-reply-warning">
          <AlertTriangle size={14} /> Auto-reply is ON — Nora is sending real replies to new customer emails without review.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(280px,380px)_1fr]">
        {/* list */}
        <div className="rounded-2xl border border-white/8 bg-[#050D1C]/50" data-testid="inbox-list">
          <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
            <span className="text-sm font-semibold text-white">Inbox</span>
            <span className="text-[11px] font-mono text-slate-500">{messages.length} messages</span>
          </div>
          <div className="max-h-[64vh] overflow-y-auto">
            {loadingList && messages.length === 0 && (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading inbox…</div>
            )}
            {!loadingList && messages.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-500">No messages in the inbox yet.</div>
            )}
            {messages.map((m) => (
              <button
                key={m.id}
                onClick={() => openMessage(m)}
                data-testid={`inbox-item-${m.id}`}
                className={cn(
                  "flex w-full flex-col gap-0.5 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/5",
                  selected?.id === m.id && "bg-white/5"
                )}
              >
                <div className="flex items-center gap-2">
                  {m.unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#00E5FF]" />}
                  <span className={cn("truncate text-sm", m.unread ? "font-semibold text-white" : "text-slate-300")}>{m.from.replace(/<.*>/, "").trim() || m.fromEmail}</span>
                  <span className="ml-auto shrink-0 text-[10px] text-slate-500">{timeAgo(m.date)}</span>
                </div>
                <div className="truncate text-xs text-slate-300">{m.subject}</div>
                <div className="truncate text-[11px] text-slate-500">{m.snippet}</div>
                {m.replied ? (
                  <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-[#22C55E]/15 px-1.5 py-0.5 text-[10px] text-[#22C55E]"><CheckCircle2 size={10} /> Replied</span>
                ) : m.acked ? (
                  <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-[#00E5FF]/15 px-1.5 py-0.5 text-[10px] text-[#00E5FF]"><MailCheck size={10} /> Nora acknowledged</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {/* detail */}
        <div className="rounded-2xl border border-white/8 bg-[#050D1C]/50 p-5" data-testid="inbox-detail">
          {!selected && (
            <div className="grid h-full min-h-[300px] place-items-center text-center text-sm text-slate-500">
              <div><Inbox size={28} className="mx-auto mb-2 text-slate-600" />Select an email to read it and reply with Nora.</div>
            </div>
          )}
          {selected && (
            <div>
              <h2 className="font-display text-lg font-semibold text-white" data-testid="detail-subject">{selected.subject}</h2>
              <div className="mt-1 text-xs text-slate-400">{selected.from} · {timeAgo(selected.date)}</div>

              {/* AI summary */}
              <div className="mt-4 rounded-xl border border-[#8A2BE2]/30 bg-[#8A2BE2]/10 p-3" data-testid="ai-summary">
                <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#C9A8FF]"><Sparkles size={12} /> Nora's summary</div>
                <div className="text-sm text-slate-200">{loadingMsg ? "Summarising…" : (selected.summary || "—")}</div>
              </div>

              {/* body */}
              <div className="mt-4 max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl border border-white/8 bg-[#030A16]/60 p-3 text-sm text-slate-300" data-testid="detail-body">
                {loadingMsg ? "Loading message…" : (selected.body || "(no text content)")}
              </div>

              {/* reply */}
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">{selected.replied ? "Reply sent" : "Reply as Nora"}</span>
                  {!selected.replied && (
                    <button onClick={generateDraft} disabled={drafting || loadingMsg} data-testid="draft-reply-btn" className="flex items-center gap-1.5 rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-3 py-1 text-xs text-[#00E5FF] disabled:opacity-50">
                      {drafting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Draft with Nora
                    </button>
                  )}
                </div>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  readOnly={selected.replied}
                  data-testid="reply-textarea"
                  placeholder="Write a reply, or let Nora draft one for you…"
                  className="h-40 w-full resize-none rounded-xl border border-white/10 bg-[#030A16]/60 p-3 text-sm text-slate-200 outline-none focus:border-[#00E5FF]/50"
                />
                {!selected.replied && (
                  <div className="mt-3 flex justify-end">
                    <button onClick={sendReply} disabled={sending || !draft.trim()} data-testid="send-reply-btn" className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-5 py-2 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                      {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send reply
                    </button>
                  </div>
                )}
                {selected.replied && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-[#22C55E]"><CheckCircle2 size={13} /> This email has been replied to.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {!status && (
        <div className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      )}
    </div>
  );
}

function PageHeader({ nora, email, onRefresh, refreshing, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {nora && <img src={nora.avatar} alt="Nora" className="h-12 w-12 rounded-xl border border-[#00E5FF]/50 object-cover" />}
        <div>
          <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">Support Inbox</h1>
          <p className="text-sm text-slate-400">Managed by Nora · {email || "cardiac.solutions.support@gmail.com"}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {onRefresh && (
          <button onClick={onRefresh} data-testid="inbox-refresh-btn" className="flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5 text-xs text-slate-300 hover:text-white">
            <RefreshCw size={13} className={cn(refreshing && "animate-spin")} /> Refresh
          </button>
        )}
        {children}
      </div>
    </div>
  );
}
